import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import * as actions from "../../../Redux/Actions";
import MainCard from "../../../component/MainCard/MainCard";
import Button from "../../../component/Buttons/Button";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import hanldeUnauthorizedUser from "../../../component/AccessConditions/UnauthorizedUser";
import { icons } from "../../../assets";
import MapSuccessorModal from "./MapSuccessorModal";
import ConfirmDialog from "../ConfirmDialog";
import "../readable-form.scss";

const empName = (e: any) =>
  [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim() || "-";

const errText = (res: any, fallback: string) =>
  res?.error?.response?.data?.error?.error_description || fallback;

/**
 * One critical role (seat) and the people mapped to it.
 *
 * Mapping only — assessing a candidate and writing their development plan live in
 * the Development Plan module. This page answers "who is in the pool for this seat".
 */
const CriticalRoleDetail = () => {
  const { unAuthorizeUser } = hanldeUnauthorizedUser();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [role, setRole] = useState<any>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<any>(null);

  // a response for a different id (fast navigation) is ignored so it can't overwrite
  // state with stale data.
  const currentId = useRef<string | undefined>(id);

  // The list endpoint already joins designation/client/incumbent; the by-id one
  // returns the bare row, so read the seat from the list to get its labels.
  const loadRole = () => {
    const rid = id;
    dispatch(
      actions.getAllCriticalRolesAction((res: any) => {
        if (rid !== currentId.current) return;
        if (res?.error) showErrorToastMessage("Couldn't load this critical role.");
        else if (res?.data) setRole(res.data.find((r: any) => r._id === id) || null);
      })
    );
  };

  const loadCandidates = () => {
    if (!id) return;
    const rid = id;
    setLoading(true);
    dispatch(
      actions.getCandidatesByCriticalRoleAction(id, (res: any) => {
        if (rid !== currentId.current) return;
        if (res?.error) showErrorToastMessage("Couldn't load the successors.");
        // Scoping is enforced server-side (a manager only receives their own team).
        else if (res?.data) setCandidates(res.data);
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    unAuthorizeUser();
    if (id) {
      currentId.current = id;
      loadRole();
      loadCandidates();
    }
  }, [id]);

  const handleRemove = (cand: any) => {
    dispatch(
      actions.removeCandidateAction(cand._id, (res: any) => {
        setConfirmRemove(null);
        if (res?.error) showErrorToastMessage(errText(res, "Failed to remove candidate"));
        else {
          showSuccessToastMessage("Candidate removed");
          loadCandidates();
        }
      })
    );
  };

  // The Designation column shows which designation's KRAs produced this score, so the
  // number needs no extra marker.
  const scoreCell = (c: any) => {
    if (c.weightedScoreSnapshot === null || c.weightedScoreSnapshot === undefined) {
      return <span className="muted">Not scored yet</span>;
    }
    return c.weightedScoreSnapshot;
  };

  const seatTitle = role?.designation?.title || "";
  const seatClient = role?.client?.clientName || "";

  return (
    <MainCard>
      <div className="assessment-heading-button-wrapper">
        <div>
          <span
            className="seat-back"
            onClick={() => navigate("/succession-planning/critical-roles")}
          >
            ← Critical Roles
          </span>
          <h2 className="mt-2">
            {seatTitle || "Critical Role"}
            {seatClient ? ` — ${seatClient}` : ""}
          </h2>
        </div>
        <div className="btn-wrapper">
          <Button
            title="+ Map Successor"
            onClick={() => setShowMap(true)}
            className="add-employee-btn"
          />
        </div>
      </div>

      {/* the seat's own attributes — context for who you're replacing */}
      <div className="seat-meta mt-2">
        {role?.tier && (
          <span>
            <b>Tier</b> {role.tier}
          </span>
        )}
        {role?.criticality && (
          <span>
            <b>Criticality</b> {role.criticality}
          </span>
        )}
        {role?.riskLevel && (
          <span>
            <b>Risk of Loss</b> {role.riskLevel}
          </span>
        )}
        {role?.incumbent && (
          <span>
            <b>Incumbent</b> {empName(role.incumbent)}
          </span>
        )}
        {role?.reason && (
          <span>
            <b>Reason</b> {role.reason}
          </span>
        )}
      </div>

      <div className="readable-table mt-3">
        <h5>
          Mapped Successors<span className="count-chip">{candidates.length}</span>
        </h5>

        {loading ? (
          <p className="mt-2">Loading…</p>
        ) : candidates.length > 0 ? (
          <div className="table table-responsive">
            <table className="table-main table table-sm mt-2">
              <thead className="table-head">
                <tr className="table-head-row">
                  <th className="table-head-row">Employee</th>
                  <th className="table-head-row">Employee ID</th>
                  <th className="table-head-row">Designation</th>
                  <th className="table-head-row">Client</th>
                  <th className="table-head-row">KRA Score</th>
                  <th className="table-head-row">Readiness</th>
                  <th className="table-head-row">Action</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {candidates.map((c) => (
                  <tr className="table-body-row" key={c._id}>
                    <td>{empName(c.employee)}</td>
                    <td>{c.employee?.employeeId || "-"}</td>
                    <td>{c.employee?.designation || "-"}</td>
                    <td>{c.employee?.clientName || "-"}</td>
                    <td>{scoreCell(c)}</td>
                    <td>{c.readiness || <span className="muted">Not assessed</span>}</td>
                    <td>
                      <div className="action-btn seat-actions">
                        <img
                          title="Remove from this role"
                          src={icons.delete_icon}
                          alt="remove"
                          className="edit-icon"
                          onClick={() => setConfirmRemove(c)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-note">
            No successors mapped yet — use <b>+ Map Successor</b> to pick from this designation,
            or search anyone in the organisation.
          </p>
        )}

        {candidates.length > 0 && (
          <p className="muted mt-2">
            Assess readiness and build development plans in <b>Development Plan</b>.
          </p>
        )}
      </div>

      <ModalComponents
        show={showMap}
        onHide={() => setShowMap(false)}
        title={`Map Successor — ${seatTitle}${seatClient ? ` (${seatClient})` : ""}`}
        modalBody={
          role ? (
            <MapSuccessorModal
              role={role}
              setShowModal={setShowMap}
              handleApiCall={loadCandidates}
            />
          ) : null
        }
        className="modal-xl"
      />

      <ModalComponents
        show={!!confirmRemove}
        onHide={() => setConfirmRemove(null)}
        title="Remove Successor"
        modalBody={
          confirmRemove ? (
            <ConfirmDialog
              message={`Remove ${empName(confirmRemove.employee)} from ${seatTitle}?`}
              detail="Their assessment and development plan for this role will be removed too."
              confirmLabel="Remove"
              onConfirm={() => handleRemove(confirmRemove)}
              onCancel={() => setConfirmRemove(null)}
            />
          ) : null
        }
        className="modal-md"
      />
    </MainCard>
  );
};

export default CriticalRoleDetail;
